import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { FrictionalPressureLossProfile } from 'src/app/perical/models/fluid.model';
import {
  GetFlowRateValueFromGpm,
  GetPressureLossFromPsi100ft,
  GetShortLengthValueFromInches
} from 'src/app/perivis/shared/helpers/units.helper';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import { greaterThanZero } from 'src/app/wellbore-inputs/components/well-configuration/validation/greaterThanZero';

export function createPolymerFrictionLossColumns(): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'tubularInnerDiameter',
      header: 'Pipe ID',
      unitKey: 'shortLengths',
      minFractionDigits: 0,
      maxFractionDigits: 3,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'flowRateOne',
      header: 'Q1',
      unitKey: 'standardFluidFlow',
      minFractionDigits: 0,
      maxFractionDigits: 3,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'pressureLossOne',
      header: 'P1',
      unitKey: 'pressureLoss',
      minFractionDigits: 0,
      maxFractionDigits: 3,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'flowRateTwo',
      header: 'Q2',
      unitKey: 'standardFluidFlow',
      minFractionDigits: 0,
      maxFractionDigits: 3,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'pressureLossTwo',
      header: 'P2',
      unitKey: 'pressureLoss',
      minFractionDigits: 0,
      maxFractionDigits: 3,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'flowRateThree',
      header: 'Q3',
      unitKey: 'standardFluidFlow',
      minFractionDigits: 0,
      maxFractionDigits: 3,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'pressureLossThree',
      header: 'P3',
      unitKey: 'pressureLoss',
      minFractionDigits: 0,
      maxFractionDigits: 3,
      formatFractionDigits: 3
    }
  ];
}

export function createPolymerFrictionLossRow(data?: Partial<FrictionalPressureLossProfile>): FormGroup {
  return new FormGroup({
    tubularInnerDiameter: new FormControl<number | null>(data?.tubularInnerDiameter ?? null, [greaterThanZero, Validators.required]),
    flowRateOne: new FormControl<number | null>(data?.flowRateOne ?? null, [greaterThanZero, Validators.required]),
    pressureLossOne: new FormControl<number | null>(data?.pressureLossOne ?? null, [greaterThanZero, Validators.required]),
    flowRateTwo: new FormControl<number | null>(data?.flowRateTwo ?? null, [greaterThanZero, Validators.required]),
    pressureLossTwo: new FormControl<number | null>(data?.pressureLossTwo ?? null, [greaterThanZero, Validators.required]),
    flowRateThree: new FormControl<number | null>(data?.flowRateThree ?? null, [greaterThanZero, Validators.required]),
    pressureLossThree: new FormControl<number | null>(data?.pressureLossThree ?? null, [greaterThanZero, Validators.required])
  });
}

export function createDefaultPolymerFrictionLossRow(userUnits: UserUnitsModel): FrictionalPressureLossProfile {
  return {
    tubularInnerDiameter: GetShortLengthValueFromInches(3.958, userUnits.shortLengths),
    flowRateOne: GetFlowRateValueFromGpm(42, userUnits.standardFluidFlow),
    pressureLossOne: GetPressureLossFromPsi100ft(1, userUnits.pressureLoss),
    flowRateTwo: GetFlowRateValueFromGpm(210, userUnits.standardFluidFlow),
    pressureLossTwo: GetPressureLossFromPsi100ft(2, userUnits.pressureLoss),
    flowRateThree: GetFlowRateValueFromGpm(4200, userUnits.standardFluidFlow),
    pressureLossThree: GetPressureLossFromPsi100ft(100, userUnits.pressureLoss)
  };
}

export function isPolymerFrictionLossRowEmpty(row: FormGroup): boolean {
  return Object.values(row.controls).every((control) => {
    const value = control.value;
    return value === null || value === undefined || value === '' || Number.isNaN(Number(value));
  });
}

export function ensureTrailingInputRow(formArray: FormArray<FormGroup>): void {
  if (formArray.length === 0) {
    formArray.push(createPolymerFrictionLossRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isPolymerFrictionLossRowEmpty(formArray.at(formArray.length - 1)) &&
    isPolymerFrictionLossRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  if (!isPolymerFrictionLossRowEmpty(formArray.at(formArray.length - 1))) {
    formArray.push(createPolymerFrictionLossRow());
  }
}

export function populatePolymerFrictionLossFormArray(
  formArray: FormArray<FormGroup>,
  profiles: readonly FrictionalPressureLossProfile[]
): void {
  formArray.clear();
  profiles.forEach((profile) => formArray.push(createPolymerFrictionLossRow(profile)));
  ensureTrailingInputRow(formArray);
}

export function extractPolymerFrictionLossRows(formArray: FormArray<FormGroup>): FrictionalPressureLossProfile[] {
  return formArray.controls
    .filter((control) => !isPolymerFrictionLossRowEmpty(control as FormGroup))
    .map((control) => ({
      tubularInnerDiameter: Number(control.get('tubularInnerDiameter')?.value ?? 0),
      flowRateOne: Number(control.get('flowRateOne')?.value ?? 0),
      pressureLossOne: Number(control.get('pressureLossOne')?.value ?? 0),
      flowRateTwo: Number(control.get('flowRateTwo')?.value ?? 0),
      pressureLossTwo: Number(control.get('pressureLossTwo')?.value ?? 0),
      flowRateThree: Number(control.get('flowRateThree')?.value ?? 0),
      pressureLossThree: Number(control.get('pressureLossThree')?.value ?? 0)
    }));
}